import React from 'react'

import type { InvoicesSummary } from '../api'
import { COUNT_UP_DURATION_MS } from '../lib/summaryConstants'
import { summariesEqual } from '../lib/invoicesSummary'
import { subscribePaidSummaryAnimation } from '../lib/summaryAnimationBus'
import { countUpDisplayAtProgress, countUpEase } from './useCountUp'

export { COUNT_UP_DURATION_MS } from '../lib/summaryConstants'

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

type Result = {
  display: InvoicesSummary | null
}

export const useAnimatedInvoicesSummary = (
  source: InvoicesSummary | null,
  isLoading: boolean,
): Result => {
  const [display, setDisplay] = React.useState<InvoicesSummary | null>(null)
  const snapshotRef = React.useRef<InvoicesSummary | null>(null)
  const hasInitializedRef = React.useRef(false)
  const animatingRef = React.useRef(false)
  const rafRef = React.useRef(0)

  const commitSnapshot = React.useCallback((summary: InvoicesSummary) => {
    snapshotRef.current = summary
  }, [])

  const startAnimation = React.useCallback((from: InvoicesSummary, to: InvoicesSummary) => {
    if (summariesEqual(from, to)) {
      setDisplay(to)
      commitSnapshot(to)
      return
    }

    cancelAnimationFrame(rafRef.current)

    if (prefersReducedMotion()) {
      setDisplay(to)
      commitSnapshot(to)
      animatingRef.current = false
      return
    }

    animatingRef.current = true
    setDisplay({
      ...from,
      dueAmount: to.dueAmount,
      dueCount: to.dueCount,
    })
    const startTime = performance.now()

    const tick = (now: number) => {
      const elapsed = now - startTime
      const t = Math.min(elapsed / COUNT_UP_DURATION_MS, 1)
      const eased = countUpEase(t)
      const nextDisplay: InvoicesSummary = {
        totalAmount: to.totalAmount,
        invoiceCount: to.invoiceCount,
        paidAmount: countUpDisplayAtProgress(from.paidAmount, to.paidAmount, 'amount', eased),
        paidCount: countUpDisplayAtProgress(from.paidCount, to.paidCount, 'count', eased),
        dueAmount: to.dueAmount,
        dueCount: to.dueCount,
      }
      setDisplay(nextDisplay)

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
        return
      }

      setDisplay(to)
      commitSnapshot(to)
      animatingRef.current = false
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [commitSnapshot])

  React.useEffect(() => {
    return subscribePaidSummaryAnimation((delta) => {
      const from = snapshotRef.current
      if (!from) return

      startAnimation(from, {
        ...from,
        paidAmount: from.paidAmount + delta.paidAmountDelta,
        paidCount: (from.paidCount ?? 0) + delta.paidCountDelta,
        dueAmount: from.dueAmount + delta.dueAmountDelta,
        dueCount: (from.dueCount ?? 0) + delta.dueCountDelta,
      })
    })
  }, [startAnimation])

  React.useLayoutEffect(() => {
    if (isLoading) return

    if (!source) {
      setDisplay(null)
      snapshotRef.current = null
      hasInitializedRef.current = false
      animatingRef.current = false
      return
    }

    if (!hasInitializedRef.current) {
      setDisplay(source)
      commitSnapshot(source)
      hasInitializedRef.current = true
      return
    }

    if (animatingRef.current) return

    const from = snapshotRef.current
    if (!from) {
      setDisplay(source)
      commitSnapshot(source)
      return
    }

    if (summariesEqual(from, source)) {
      setDisplay(source)
      return
    }

    setDisplay(source)
    commitSnapshot(source)
  }, [source, isLoading, commitSnapshot])

  React.useEffect(() => () => {
    cancelAnimationFrame(rafRef.current)
  }, [])

  if (isLoading || !source) {
    return { display: null }
  }

  if (!hasInitializedRef.current) {
    return { display: source }
  }

  return { display: display ?? source }
}
